/**
 * AutoSaveStatus Component
 * Displays auto-save status as an icon with tooltip
 */

import React from 'react';
import { Box, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import { formatDistanceToNow } from 'date-fns';
import type { AutoSaveStatus as AutoSaveStatusType } from '../../hooks/useAutoSave';

export interface AutoSaveStatusProps {
  status: AutoSaveStatusType;
  lastSaved: Date | null;
  isDirty?: boolean; // Whether there are unsaved changes
}

export const AutoSaveStatus: React.FC<AutoSaveStatusProps> = ({
  status,
  lastSaved,
  isDirty = false,
}) => {
  const getStatusContent = () => {
    // Status 'saving' and 'saved' take priority over isDirty
    switch (status) {
      case 'saving':
        return {
          tooltip: 'Saving...',
          color: '#999',
          icon: <SyncIcon sx={{ fontSize: 16, color: '#999', animation: 'spin 1s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />,
        };
      case 'saved':
        return {
          tooltip: lastSaved ? `Saved ${formatDistanceToNow(lastSaved, { addSuffix: true })}` : 'Saved',
          color: '#4caf50',
          icon: <CheckCircleIcon sx={{ fontSize: 16, color: '#4caf50' }} />,
        };
      case 'error':
        return {
          tooltip: 'Error saving',
          color: '#f44336',
          icon: <ErrorIcon sx={{ fontSize: 16, color: '#f44336' }} />,
        };
      case 'idle':
      default:
        // Only show unsaved when idle and dirty
        if (isDirty) {
          return {
            tooltip: 'Unsaved changes',
            color: '#FED402',
            icon: <EditIcon sx={{ fontSize: 16, color: '#FED402' }} />,
          };
        }
        if (lastSaved) {
          return {
            tooltip: `Saved ${formatDistanceToNow(lastSaved, { addSuffix: true })}`,
            color: '#666',
            icon: <CheckCircleIcon sx={{ fontSize: 16, color: '#666' }} />,
          };
        }
        return null;
    }
  };

  const content = getStatusContent();

  if (!content) {
    return null;
  }

  return (
    <Tooltip title={content.tooltip} arrow placement="bottom">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '6px',
          borderRadius: '4px',
          cursor: 'default',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
        }}
      >
        {content.icon}
      </Box>
    </Tooltip>
  );
};
